import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { API_ENDPOINTS } from 'core/constant/API_ENDPOINTS';
import DataTableWrapper from 'components/common/DataTableWrapper';
import ConfirmDeleteModal from 'components/common/modal/ConfirmDeleteModal';
import EditProductOptionsModal from 'components/admin/common/modal/product/EditProductOptionsModal';
import { typeOptionProduct, formatDisplay, colorSelect } from 'core/constant/SELECT_OPTIONS';
import useModalState from 'core/hooks/state/useModalState';
import { validateForm } from 'core/utils/validation';
import useCrud from 'core/hooks/api/useCrud';
import { Alert } from 'react-bootstrap';
import usePost from 'core/hooks/api/usePost';
import useDelete from 'core/hooks/api/useDelete';
import moment from 'moment';
import ImageWithBaseApi from 'components/common/mediaRender/ImageWithBaseApi';

const ProductOptions = () => {
    const {
        fetchData: data,
        fetchError: error,
        refetch,
        postData: createProductOptionData,
        postError: creatingError,
        postLoading: creating,
        putData: updateProductOptionData,
        putError: updatingError,
        putLoading: updating,
        deleteData: deleteProductOptionData,
        deleteLoading: deleting,
        deleteError: deletingError,
        clearErrors: clearServerErrors
    } = useCrud(API_ENDPOINTS.PRODUCT_OPTIONS);

    const { fetchData: productData } = useCrud(API_ENDPOINTS.PRODUCTS);
    const { postData: createProductPhoto, loading: uploadingImages } = usePost(API_ENDPOINTS.PRODUCT_APPLIED_PHOTOS);
    const { deleteData: deleteProductPhoto } = useDelete(API_ENDPOINTS.PRODUCT_APPLIED_PHOTOS);
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState(null);

    const handleCloseDeleteProductOption = () => {
        setErrors({});
        clearServerErrors();
    };

    const handleCloseEditProductOption = () => {
        setErrors({});
        clearServerErrors();
    };

    const { isOpen: isOpenDeleteProductOption, modalData: productOptionToDelete, openModal: openDeleteProductOption, closeModal: closedDeleteProductOption } = useModalState({
        onClose: handleCloseDeleteProductOption
    });

    const { isOpen: isOpenEditProductOption, modalData: selectedProductOption, openModal: openEditProductOption, closeModal: closedEditProductOption } = useModalState({
        onClose: handleCloseEditProductOption
    });

    const validationRules = [
        { field: 'display_type', method: 'required' },
        { field: 'product_id', method: 'required' },
        { field: 'type', method: 'required' },
        { field: 'value', method: 'required' },
        { field: 'url', method: 'imageFormat' },

    ];

    const productOptions = useMemo(() => {
        if (productData?.length > 0) {
            return productData.map(client => ({
                label: client.title,
                value: client.id
            }));
        } else {
            return [{ label: 'Default', value: 1 }, { label: 'Default', value: 2 }];
        }
    }, [productData]);

    const handleDeleteProductPhoto = async (id) => {
        try {
            await deleteProductPhoto(id);
            refetch();
        } catch (error) {
            console.error(error);
        }
    };

    const handleModalSave = useCallback(async (updatedProductOption, galleryImages) => {
        setSuccessMessage(null);
        clearServerErrors();
        const formErrors = validateForm(updatedProductOption, validationRules);
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            return;
        }

        setErrors({});
        const forms = galleryImages
            ?.filter((image) => {
                const fileType = image.file?.type || image.file?.path.split('.').pop();
                return image.file && image.file?.path && !image.file.path.startsWith('storage/') && (fileType.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif'].includes(fileType));
            })
            ?.map((image) => ({
                product_id: updatedProductOption?.product_id || null,
                option_id: updatedProductOption.id || null,
                type: 'image',
                src: image.file,
            }));

        try {
            const formattedProductOption = {
                ...updatedProductOption,
                date: moment(updatedProductOption.date).format('YYYY-MM-DD')
            };

            if (updatedProductOption.id) {
                const { applied_photos, product_option, ...productOptionData } = formattedProductOption;
                await updateProductOptionData(updatedProductOption.id, productOptionData, true);
                if (forms.length > 0) {
                    for (const form of forms) {
                        form.option_id = updatedProductOption.id;
                        await createProductPhoto(form, true);
                    }
                }
                setSuccessMessage("Opción de producto actualizada con éxito.");
            } else {
                const createdProductOption = await createProductOptionData(formattedProductOption, true);
                if (forms.length > 0) {
                    for (const form of forms) {
                        form.option_id = createdProductOption.id;
                        await createProductPhoto(form, true);
                    }
                }
                setSuccessMessage("Opción de producto creada con éxito.");
            }
            closedEditProductOption();
            setErrors({});
            refetch();
        } catch (error) {
            console.error(error);
        }
    }, [updateProductOptionData, createProductOptionData, createProductPhoto, refetch]);

    const handleCreateClick = useCallback(() => {
        setErrors({});
        const newProductOption = {
            title: '',
            category: '',
            banner_image: '',
            date: '',
            location: '',
            description: '',
            client_id: ''
        };
        openEditProductOption(newProductOption);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        setSuccessMessage(null);
        if (productOptionToDelete) {
            try {
                await deleteProductOptionData(productOptionToDelete.id);
                setSuccessMessage("Opción de producto eliminada con éxito.");
                refetch();
                closedDeleteProductOption();
            } catch (error) {
                console.error(error);
            }
        }
    }, [productOptionToDelete, deleteProductOptionData, refetch]);

    const columns = useMemo(() => [
        { field: 'product_title', header: 'Nombre del Producto', sortable: true },
        {
            field: 'type',
            header: 'Tipo de Variante',
            sortable: true,
            body: (rowData) => {
                const typeOption = typeOptionProduct.find(option => option.value === rowData.type);
                return typeOption ? typeOption.label : rowData.type;
            }
        },
        {
            field: 'display_type',
            header: 'Formato',
            sortable: true,
            body: (rowData) => {
                const displayOption = formatDisplay.find(option => option.value === rowData.display_type);
                return displayOption ? displayOption.label : rowData.display_type;
            }
        },
        {
            field: 'value',
            header: 'Valor de la variante',
            sortable: true,
            body: (rowData) => {
                if (rowData.url) {
                    return <ImageWithBaseApi src={rowData.url} alt={rowData.value} width="50" height="50" />;
                } else if (rowData.type === 'color') {
                    const colorOption = colorSelect.find(option => option.value === rowData.value);
                    return colorOption ? (
                        <div style={{ backgroundColor: colorOption.color, width: '50px', height: '50px' }}></div>
                    ) : rowData.value;
                } else {
                    return <span style={{ textTransform: 'capitalize' }}>{rowData.value}</span>;
                }
            }
        }
    ], []);

    const memoizedGalleryImages = useMemo(() => selectedProductOption?.applied_photos || [], [selectedProductOption]);

    if (error) return <p>Error: {error.message}</p>;

    return (
        <div>
            <div className="row w-100 mb-2">
                <div className="p-0 sub-title Service mb-2">
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <h5 className="m-0 text-title">Opciones de Producto</h5>
                        <button className="btn btn-primary" onClick={handleCreateClick} disabled={updating || creating}>
                            <i className="fa fa-plus-circle me-2" aria-hidden="true" />
                            Agregar Opción de Producto
                        </button>
                    </div>
                </div>
                <hr className="mx-0" style={{ border: '2px', margin: '5px' }} />
            </div>
            {successMessage && <Alert variant="success">{successMessage}</Alert>}
            <DataTableWrapper
                data={data || []}
                columns={columns}
                onEdit={openEditProductOption}
                onDelete={openDeleteProductOption}
                paginator={false}
                totalRecords={data?.length || 0}
                emptyMessage="No se encontraron registros."
                sortField="title"
                sortOrder={1}
                lazy={false}
                showGridlines={true}
            />
            <EditProductOptionsModal
                show={isOpenEditProductOption}
                productOption={selectedProductOption}
                handleClose={closedEditProductOption}
                handleSave={handleModalSave}
                updating={updating || creating || uploadingImages}
                errors={errors}
                requestError={creatingError || updatingError}
                deleteMediaItem={handleDeleteProductPhoto}
                initialImages={memoizedGalleryImages}
                productOptions={productOptions}
            />
            <ConfirmDeleteModal
                show={isOpenDeleteProductOption}
                onHide={closedDeleteProductOption}
                onConfirm={handleDeleteConfirm}
                deleting={deleting}
                error={deletingError}
            />
        </div>
    );
};

export default React.memo(ProductOptions);